import { useMemo } from "react";
import GlobalBadge from "../../../../../global/GlobalBadge";
import { GlobalButton } from "../../../../../global/forms/GlobalFormFields";
import {
  GlobalDirectoryShell,
  GlobalDirectoryFiltersSection,
  GlobalDirectoryGridSection,
  GlobalDirectoryPaginationSection,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
} from "../../../../../global/directory";
import {
  MEMBERSHIP_LIST_COLUMN_DEFS,
  MEMBERSHIP_LIST_COLUMN_VISIBILITY_KEY,
  MEMBERSHIP_LIST_PAGE_SIZE,
} from "../membershipListConstants";
import { isTruthy, membershipCellValue } from "../membershipListHelpers";
import "../../../styles/VolunteerTable.css";
import "../../../styles/membershipList.css";

export default function MembershipListDirectorySection({
  draftFilters,
  setDraftFilters,
  columnFilterOptions,
  valueFilterOptions,
  columnVisibility,
  onVisibilityChange,
  visibleColumns,
  sortedRows,
  loading,
  sortColumn,
  sortOrder,
  onSortHeaderClick,
  page,
  totalPages,
  total,
  onPageChange,
  onViewRow,
  onEditRow,
}) {
  const tableHeaders = useMemo(
    () => [
      ...visibleColumns.map((c) => ({
        key: c.id,
        label: c.sortable ? (
          <button type="button" className="vpart-sort-btn" onClick={() => onSortHeaderClick(c)}>
            {c.label}
            {sortColumn === c.id ? (
              <span className="vpart-sort-ind">{sortOrder === "ASC" ? "▲" : "▼"}</span>
            ) : null}
          </button>
        ) : (
          c.label
        ),
      })),
      { key: "actions", label: "Actions", className: "vt-col-actions" },
    ],
    [visibleColumns, sortColumn, sortOrder, onSortHeaderClick],
  );

  const getExportData = () => {
    const cols = visibleColumns;
    if (!cols.length) return { headers: [], rows: [] };
    return {
      headers: cols.map((c) => c.label),
      rows: sortedRows.map((r) =>
        cols.map((c) => {
          const raw =
            c.id === "application_verified" || c.id === "payment_verified"
              ? isTruthy(r[c.id])
                ? "Verified"
                : "Not verified"
              : membershipCellValue(c.id, r);
          return raw == null ? "" : String(raw);
        }),
      ),
    };
  };

  const filters = (
    <GlobalDirectoryFiltersSection
      className="ub-filters ub-user-directory-filters"
      searchLabel="Search by student name"
      searchPlaceholder="Type student name"
      searchValue={draftFilters.student_search}
      onSearchChange={(v) => setDraftFilters((f) => ({ ...f, student_search: v }))}
      columnLabel="Column"
      columnValue={draftFilters.column}
      onColumnChange={(v) => setDraftFilters((f) => ({ ...f, column: v, value: "" }))}
      columnOptions={columnFilterOptions}
      columnPlaceholderOption="Select"
      valueLabel="Value"
      valueValue={draftFilters.value}
      onValueChange={(v) => setDraftFilters((f) => ({ ...f, value: v }))}
      valueOptions={valueFilterOptions}
      valuePlaceholderOption="All"
      onClear={() => setDraftFilters((f) => ({ ...f, column: "", value: "" }))}
      clearFilterLabel="Clear filter"
      secondaryStart={
        <GlobalDirectoryColumnPicker
          columnDefs={MEMBERSHIP_LIST_COLUMN_DEFS}
          visibility={columnVisibility}
          onVisibilityChange={onVisibilityChange}
          storageKey={MEMBERSHIP_LIST_COLUMN_VISIBILITY_KEY}
        />
      }
      secondaryEnd={
        <GlobalDirectoryExportMenu
          getExportData={getExportData}
          filenameBase={`memberships-${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`}
          buttonLabel="Export as"
        />
      }
    />
  );

  const grid = (
    <GlobalDirectoryGridSection
      loading={loading}
      loadingMessage="Loading memberships…"
      headers={tableHeaders}
      isEmpty={!loading && sortedRows.length === 0}
      emptyMessage="No membership applications found."
      tableAriaLabel="Volunteer memberships list"
      wrapClassName="vt-table-wrap"
      tableClassName="vt-table"
    >
      {sortedRows.map((row) => (
        <tr key={row.application_id}>
          {visibleColumns.map((c) => (
            <td key={c.id}>
              {c.id === "application_verified" ? (
                <GlobalBadge
                  active={isTruthy(row.application_verified)}
                  activeText="Verified"
                  inactiveText="Not verified"
                />
              ) : c.id === "payment_verified" ? (
                <GlobalBadge
                  active={isTruthy(row.payment_verified)}
                  activeText="Verified"
                  inactiveText="Not verified"
                />
              ) : c.id === "student_name" ? (
                <span style={{ fontWeight: 600 }}>{membershipCellValue(c.id, row)}</span>
              ) : (
                membershipCellValue(c.id, row)
              )}
            </td>
          ))}
          <td className="vt-col-actions">
            <div className="vt-actions">
              <GlobalButton type="button" variant="ghost" size="sm" onClick={() => onViewRow(row)}>
                View
              </GlobalButton>
              {onEditRow ? (
                <GlobalButton type="button" variant="ghost" size="sm" onClick={() => onEditRow(row)}>
                  Edit
                </GlobalButton>
              ) : null}
            </div>
          </td>
        </tr>
      ))}
    </GlobalDirectoryGridSection>
  );

  const pagination = (
    <GlobalDirectoryPaginationSection
      page={page}
      totalPages={totalPages}
      total={total}
      pageSize={MEMBERSHIP_LIST_PAGE_SIZE}
      onPageChange={onPageChange}
      label="Membership applications pagination"
    />
  );

  return (
    <GlobalDirectoryShell
      filters={filters}
      grid={grid}
      pagination={pagination}
      className="vml-directory-shell"
    />
  );
}
